import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import BackToTop from './components/BackToTop';
import HeroCarousel from './components/HeroCarousel';
import TrustBenefitsBar from './components/TrustBenefitsBar';
import CategoriesSection from './components/CategoriesSection';
import ProductShowcase from './components/ProductShowcase';
import ProductCard from './components/ProductCard';
import MidBanner from './components/MidBanner';
import AboutSection from './components/AboutSection';
import BrandCarousel from './components/BrandCarousel';
import InstagramFeed from './components/InstagramFeed';
import Footer from './components/Footer';
import { productsApi, bannersApi, settingsApi, categoriesApi } from './services/api';
import { useRecentlyViewed } from './hooks/useRecentlyViewed';
import SEO from './components/SEO';

function App() {
  const [products, setProducts] = useState([]);
  const [exclusiveProducts, setExclusiveProducts] = useState([]);
  const [exclusiveLoading, setExclusiveLoading] = useState(true);
  const [showAllExclusive, setShowAllExclusive] = useState(false);
  const [newProducts, setNewProducts] = useState([]);
  const [newProductsLoading, setNewProductsLoading] = useState(true);
  const [promoBanners, setPromoBanners] = useState([]);
  const [promoBannersLoading, setPromoBannersLoading] = useState(true);
  const [, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [featuredCategoryProducts, setFeaturedCategoryProducts] = useState([]);
  const [featuredCategoryTitle, setFeaturedCategoryTitle] = useState('');
  const [featuredCategoryLink, setFeaturedCategoryLink] = useState('');
  const [featuredCategoryLoading, setFeaturedCategoryLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setError(null);
        const data = await productsApi.getAll();
        if (data && data.length > 0) {
          setProducts(data);
        } else {
          setError('No products available');
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchNewProducts = async () => {
      try {
        const data = await productsApi.getNew();
        setNewProducts(data || []);
      } catch (error) {
        console.warn('Failed to fetch new products, using recent products as fallback');
        // Fallback to recent products if new products API fails
        if (products.length > 0) {
          setNewProducts(products.slice(4, 12)); // Show next 8 products as fallback
        }
      } finally {
        setNewProductsLoading(false);
      }
    };

    if (products.length > 0) {
      fetchNewProducts();
    }
  }, [products]);

  useEffect(() => {
    const fetchExclusiveProducts = async () => {
      try {
        const data = await productsApi.getExclusive();
        setExclusiveProducts(data || []);
      } catch (error) {
        console.warn('Failed to fetch exclusive products:', error);
        setExclusiveProducts([]);
      } finally {
        setExclusiveLoading(false);
      }
    };
    fetchExclusiveProducts();
  }, []);

  useEffect(() => {
    const fetchPromoBanners = async () => {
      try {
        const data = await bannersApi.getPromoBanners();
        setPromoBanners(data || []);
      } catch (error) {
        console.warn('Failed to fetch promo banners:', error);
        setPromoBanners([]);
      } finally {
        setPromoBannersLoading(false);
      }
    };
    fetchPromoBanners();
  }, []);

  useEffect(() => {
    const fetchFeaturedCategory = async () => {
      try {
        const settings = await settingsApi.getPublic();

        const slug = settings.featuredCategorySlug;

        if (slug) {
          const [categoryProducts, categoryData] = await Promise.all([
            productsApi.getByCategory(slug, 4),
            categoriesApi.getBySlug(slug),
          ]);

          // Fallback: if no products in featured category, use fallback
          if (!categoryProducts || categoryProducts.length === 0) {
            const allProducts = await productsApi.getAll();
            setFeaturedCategoryProducts(allProducts.slice(0, 6));
            setFeaturedCategoryTitle("This Week's Picks");
            setFeaturedCategoryLink('/shop-all');
          } else {
            setFeaturedCategoryProducts(categoryProducts);
            setFeaturedCategoryTitle(`${categoryData?.name || slug} Week`);
            setFeaturedCategoryLink(`/${slug}`);
          }
        } else {
          const allProducts = await productsApi.getAll();
          setFeaturedCategoryProducts(allProducts.slice(0, 6));
          setFeaturedCategoryTitle("This Week's Picks");
          setFeaturedCategoryLink('/shop-all');
        }
      } catch (error) {
        console.error('FETCH FAILED:', error);
      } finally {
        setFeaturedCategoryLoading(false);
      }
    };
    fetchFeaturedCategory();
  }, []);

  const location = useLocation();
  const { recentlyViewed } = useRecentlyViewed();
  const isShopAll = location.pathname === '/shop-all';

  if (isShopAll) {
    return (
      <>
      <SEO
        title={null}
        description="Shop premium beauty and cosmetics at Luxe Looks. Lipsticks, skincare, foundations and more delivered across Kenya."
        url="/"
      />
      <div className='min-h-screen font-sans'>
        <BackToTop />
        <Header />
        <div className='max-w-7xl mx-auto px-4 py-8'>
          <h1 className='text-3xl font-bold mb-2'>Shop All</h1>
          <p className='text-gray-500 mb-8'>Browse our complete collection</p>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6'>
            {products.map((product, index) => (
              <ProductCard key={index} product={product} />
            ))}
          </div>
        </div>
        <Footer />
      </div>
    </>
    );
  }

  return (
    <>
      <SEO
        title={null}
        description="Shop premium beauty and cosmetics at Luxe Looks. Lipsticks, skincare, foundations and more delivered across Kenya."
        url="/"
      />
      <div className='min-h-screen font-sans'>
      <BackToTop />
      <Header />
      <HeroCarousel />
      <TrustBenefitsBar />
      <CategoriesSection />
       <ProductShowcase
          title={featuredCategoryTitle || "This Week's Picks"}
          products={featuredCategoryProducts}
          seeMoreLink={featuredCategoryLink}
          loading={featuredCategoryLoading}
        />
       {promoBannersLoading ? (
         <div className="w-full h-48 bg-gray-200 animate-pulse my-8">
         </div>
       ) : promoBanners[0] && (
         <MidBanner
           title={promoBanners[0].title}
           subtitle={promoBanners[0].subtitle}
           cta={promoBanners[0].cta}
           image={promoBanners[0].image_url}
         />
       )}
       <ProductShowcase 
           title='Online Exclusive' 
           products={showAllExclusive ? exclusiveProducts : exclusiveProducts.slice(0, 6)} 
           showSeeMore={false} 
           loading={exclusiveLoading}
        />
        {exclusiveProducts.length > 4 && (
          <div className="flex justify-center mt-4">
            <button 
              onClick={() => setShowAllExclusive(!showAllExclusive)}
              className="px-6 py-2 border border-gold text-gold font-medium rounded-lg hover:bg-gold hover:text-white transition-colors"
            >
              {showAllExclusive ? 'Show Less' : `View All (${exclusiveProducts.length})`}
            </button>
          </div>
        )}
       {promoBannersLoading ? (
         <div className="w-full h-48 bg-gray-200 animate-pulse my-8">
         </div>
       ) : promoBanners[1] && (
         <MidBanner
           title={promoBanners[1].title}
           subtitle={promoBanners[1].subtitle}
           cta={promoBanners[1].cta}
           image={promoBanners[1].image_url}
         />
       )}
      <ProductShowcase
        title="What's New"
        products={newProducts.slice(0, 6)}
        subtitle="Discover our latest arrivals and trending products"
        loading={newProductsLoading}
        badge="NEW"
      />
      <AboutSection />
      <BrandCarousel />
      <InstagramFeed />
      {recentlyViewed.length > 0 && (
        <ProductShowcase
          title="Recently Viewed"
          products={recentlyViewed}
          seeMoreLink="/shop-all"
        />
      )}
      <Footer />
    </div>
    </>
  );
}

export default App;